import React, { useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import type { MarketplaceLaunchContext, MarketplaceTabId } from '../../types/windows'
import { DoctorMarketplace } from './DoctorMarketplace'
import { FarmMarketplace } from './FarmMarketplace'
import logoShort from '../../images/Logo Short.jpg'
import './Marketplace.css'

interface MarketplaceProps {
  launchContext?: MarketplaceLaunchContext
}

const normalizeMarketplaceTab = (tab: MarketplaceTabId): MarketplaceTabId => tab

export const Marketplace: React.FC<MarketplaceProps> = ({ launchContext }) => {
  const { userType } = useAuth()
  const [activeTab, setActiveTab] = useState<MarketplaceTabId>('browse')
  const [doctorUnread, setDoctorUnread] = useState(0)
  const [farmUnread, setFarmUnread] = useState(0)
  const [pendingJobId, setPendingJobId] = useState<string | null>(null)

  const isDoctor = userType === 'doctor'
  const isFarm = userType === 'farm'

  useEffect(() => {
    setActiveTab(isFarm ? 'dashboard' : 'browse')
  }, [isFarm])

  useEffect(() => {
    if (isDoctor && launchContext?.initialTab) {
      setActiveTab(normalizeMarketplaceTab(launchContext.initialTab))
    }
  }, [isDoctor, launchContext])

  const normalizedActiveTab = normalizeMarketplaceTab(activeTab)

  const handleOpenJobFromInbox = (jobId: string) => {
    setPendingJobId(jobId)
    if (isFarm) {
      setActiveTab('browse')
    } else if (isDoctor) {
      setActiveTab('my-jobs')
    }
  }

  return (
    <div className="marketplace-container">
      <div className="marketplace-header">
        <h1><img src={logoShort} alt="Girasoles" className="marketplace-logo" /> Marketplace</h1>
        <p className="subtitle">
          {isDoctor ? 'Create print jobs and select farms' : 'Browse available jobs and submit bids'}
        </p>
      </div>

      {isDoctor && (
        <div className="marketplace-tabs">
          <button className={`tab-button ${normalizedActiveTab === 'browse' ? 'active' : ''}`} onClick={() => setActiveTab('browse')}>Browse Farms</button>
          <button className={`tab-button ${normalizedActiveTab === 'my-jobs' ? 'active' : ''}`} onClick={() => setActiveTab('my-jobs')}>My Jobs</button>
          <button className={`tab-button ${normalizedActiveTab === 'inbox' ? 'active' : ''}`} onClick={() => setActiveTab('inbox')}>Inbox{doctorUnread > 0 && <span className="tab-badge">{doctorUnread}</span>}</button>
          <button className={`tab-button ${normalizedActiveTab === 'girasoles-assistant' ? 'active' : ''}`} onClick={() => setActiveTab('girasoles-assistant')}>AI Assistant</button>
        </div>
      )}

      {isFarm && (
        <div className="marketplace-tabs">
          <button className={`tab-button ${normalizedActiveTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>Dashboard</button>
          <button className={`tab-button ${normalizedActiveTab === 'browse' ? 'active' : ''}`} onClick={() => setActiveTab('browse')}>Available Jobs</button>
          <button className={`tab-button ${normalizedActiveTab === 'active-jobs' ? 'active' : ''}`} onClick={() => setActiveTab('active-jobs')}>Active Jobs</button>
          <button className={`tab-button ${normalizedActiveTab === 'my-bids' ? 'active' : ''}`} onClick={() => setActiveTab('my-bids')}>My Bids</button>
          <button className={`tab-button ${normalizedActiveTab === 'inbox' ? 'active' : ''}`} onClick={() => setActiveTab('inbox')}>Inbox{farmUnread > 0 && <span className="tab-badge">{farmUnread}</span>}</button>
          <button className={`tab-button ${normalizedActiveTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>Settings</button>
          <button className={`tab-button ${normalizedActiveTab === 'girasoles-assistant' ? 'active' : ''}`} onClick={() => setActiveTab('girasoles-assistant')}>AI Assistant</button>
          <button className={`tab-button ${normalizedActiveTab === 'presets' ? 'active' : ''}`} onClick={() => setActiveTab('presets')}>Presets</button>
        </div>
      )}

      <div className="marketplace-content">
        {isDoctor ? (
          <DoctorMarketplace
            activeTab={normalizedActiveTab as MarketplaceTabId}
            launchContext={launchContext}
            onUnreadCountChange={setDoctorUnread}
            onOpenJob={handleOpenJobFromInbox}
            pendingJobId={pendingJobId}
            onPendingJobConsumed={() => setPendingJobId(null)}
          />
        ) : isFarm ? (
          <FarmMarketplace
            activeTab={normalizedActiveTab as 'browse' | 'my-jobs' | 'active-jobs' | 'my-bids' | 'inbox' | 'dashboard' | 'settings' | 'girasoles-assistant' | 'presets'}
            onUnreadCountChange={setFarmUnread}
            onOpenJob={handleOpenJobFromInbox}
            pendingJobId={pendingJobId}
            onPendingJobConsumed={() => setPendingJobId(null)}
          />
        ) : (
          <div className="marketplace-placeholder">
            <p>Marketplace feature coming soon for your account type</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default Marketplace
